// ============================================================
//  Deterministischer Generator (Umbau §7) — ROHDATEN wie aus einem Systemexport.
//  Richtung: Regelwerk → Einzelrollen (tragen Werte) → Sammelrollen (bündeln)
//  → Konten (je System+Mandant) → Personen (über Schlüssel). Die KLASSE ist
//  danach kein Mock, sondern läuft als Funktion (classification.ts) über die
//  Rohdaten. Kalibrierung (§8) nur über Kohortengrößen, nie über die Logik.
//
//  Iteration 3 — Realismus der Daten:
//  · Rollen gehören zu GENAU EINEM System. Fachliche Vorlagen werden je System
//    instanziiert (Z_FI_BUCHEN_GP7, Z_FI_BUCHEN_CS7, …) — wie beim Kunden.
//  · Personen haben ein bis fünf Landschaften, gemischte Klassen je System.
//  · Jede Rolle trägt mehrere lizenzrelevante Werte aus dem SAP-Namensraum;
//    zusätzlich eine plausible Gesamtzahl an Objekten (die meisten ohne Lizenzwirkung).
//  · Jede entfernbare Rolle auf jedem System ist eine eigene Maßnahme. Ihr Aufwand
//    wird aus der Trace-Mischung BERECHNET: viel Nutzung = Rolle neu schneiden = hoch.
//  · Kohortengrößen sind krumm. Die Logik ist unverändert.
// ============================================================

import type {
  Account,
  AuthValue,
  CompositeRole,
  EngineMetric,
  Entitlement,
  Landscape,
  Lever,
  LeverCluster,
  LicenseClass,
  Person,
  PersonType,
  Role,
  SingleRole,
  Snapshot,
  TraceState,
} from "../domain/types";
import { DEFAULT_RATIO, mulberry32, weight } from "../domain/rules";
import { classifyValues } from "../domain/classification";
import { RULESET } from "./pce-ruleset";
import { assignedLandscapes, landscapes } from "./landscapes";
import { isFueRelevantSid, licensingBySid } from "./licensing";
import { contractedFue } from "../store/contract";
import { TRACE_META } from "./traceMeta";
import { buildDataset } from "../domain/build";

const SEED = 20260925;

export interface Dataset {
  persons: Person[];
  accounts: Account[];
  roles: Role[];
  singleRoles: SingleRole[];
  compositeRoles: CompositeRole[];
  levers: Lever[];
  entitlements: Entitlement[];
  snapshots: Snapshot[];
  landscapes: Landscape[];
  technicalCount: number;
  meta: { generatedFrom: number; accountsTotal: number; pceVersion: string; source?: "generator" | "seed"; generatedAt?: string };
}

// ---- reale Berechtigungswerte aus dem Regelwerk ziehen (nie erfunden) -------
function ruleAV(obj: string, val?: string): AuthValue {
  const r = RULESET.find((x) => x.obj === obj && (val === undefined || x.val === val));
  if (!r) throw new Error(`Kein PCE-Wert für ${obj} ${val ?? ""}`);
  return { obj: r.obj, field: r.field, val: r.val };
}

// ============================================================
//  Rollen-VORLAGEN (fachlich). Instanziiert werden sie je System.
//  Alle Werte sind klassische SAP-Objekte aus dem Regelwerk V1.70.
// ============================================================
interface SingleTemplate {
  key: string;
  name: string;
  values: AuthValue[];
  objectsTotal: number; // Objekte in der Rolle insgesamt (nur `values` sind lizenzrelevant)
  removable?: boolean;
  cluster?: LeverCluster;
  rationale?: string;
  /**
   * Rolle ohne gepflegten Kurztext (AGR_TEXTS leer) — im echten Berechtigungswesen der Normalfall
   * für einen Teil der Rollen. Die App zeigt dann nur den technischen Namen. `name` bleibt hier
   * als fachliche Beschreibung für Leser des Codes stehen, geht aber nicht in die Daten.
   */
  ohneKurztext?: boolean;
}

const T = {
  // --- Basisrollen (nicht entfernbar) ---
  FI_BUCHEN: { key: "FI_BUCHEN", name: "Buchhaltung buchen", objectsTotal: 47,
    values: [ruleAV("F_BKPF_BUK", "06"), ruleAV("F_BKPF_BUK", "07"), ruleAV("F_BKPF_KOA", "06")] },
  VERTRIEB: { key: "VERTRIEB", name: "Vertrieb Auftragsanlage", objectsTotal: 38,
    values: [ruleAV("V_VBAK_AAT", "01"), ruleAV("V_VBRK_VKO", "01"), ruleAV("V_VBRK_FKA", "C1")] },
  HANDEL: { key: "HANDEL", name: "Handel Abrechnung", objectsTotal: 29, ohneKurztext: true,
    values: [ruleAV("W_WBRK_ORG", "01"), ruleAV("W_WBRK_ORG", "85")] },
  FI_ANZEIGE: { key: "FI_ANZEIGE", name: "Buchhaltung Anzeige", objectsTotal: 31,
    values: [ruleAV("F_BKPF_BUK", "03"), ruleAV("F_KNA1_APP", "03"), ruleAV("F_BKPF_BLA", "03")] },
  EINKAUF_ANZ: { key: "EINKAUF_ANZ", name: "Einkauf Anzeige", objectsTotal: 26,
    values: [ruleAV("M_BEST_WRK", "03"), ruleAV("M_BEST_EKO", "03"), ruleAV("M_MATE_WRK", "03")] },
  LAGER_BEWEG: { key: "LAGER_BEWEG", name: "Lagerbewegung", objectsTotal: 42,
    values: [ruleAV("M_MSEG_WWA", "01"), ruleAV("M_MSEG_BWA", "02"), ruleAV("V_LIKP_VST", "01")] },
  WF_ADMIN: { key: "WF_ADMIN", name: "Workflow-Administration", objectsTotal: 53,
    values: [ruleAV("S_WFFLXDEF", "01"), ruleAV("S_WFFLXDEF", "02")] },
  ADMIN_BASIS: { key: "ADMIN_BASIS", name: "Administration Basis", objectsTotal: 61,
    values: [ruleAV("F_FIGM_GNT", "02"), ruleAV("S_WFFLXDEF", "61")] },
  IT_MONITOR: { key: "IT_MONITOR", name: "Monitoring & Anzeige", objectsTotal: 36, ohneKurztext: true,
    values: [ruleAV("S_WFFLXDEF", "03"), ruleAV("S_USER_GRP", "02"), ruleAV("F_BKPF_BUK", "03")] },
  // --- Vermeidbare Über-Grants (jede Instanz je System ist eine Maßnahme) ---
  FI_VOLL: { key: "FI_VOLL", name: "Buchhaltung Vollzugriff", objectsTotal: 58, removable: true, cluster: "Lager",
    values: [ruleAV("F_BKPF_BUK", "22"), ruleAV("F_BKPF_BUK", "43")],
    rationale: "F_BKPF_BUK ACTVT 22/43 (Advanced). Dasselbe Objekt liegt als ACTVT 06/07 nur auf Core — der Wert entscheidet, nicht das Objekt." },
  BELEG_VOLL: { key: "BELEG_VOLL", name: "Belegwesen Vollzugriff", objectsTotal: 44, removable: true, cluster: "Büro",
    values: [ruleAV("F_BKPF_BLA", "22"), ruleAV("F_BKPF_KOA", "43")],
    rationale: "F_BKPF_BLA ACTVT 22 und F_BKPF_KOA ACTVT 43 (beide Advanced) — Belegvollzugriff, den Sachbearbeitung selten braucht." },
  WF_SONDER: { key: "WF_SONDER", name: "Workflow-Sonderrechte", objectsTotal: 22, removable: true, cluster: "Externe",
    values: [ruleAV("S_WFFLXDEF", "06"), ruleAV("S_WFFLXDEF", "60")],
    rationale: "S_WFFLXDEF ACTVT 06/60 (Advanced, Workflow-Definition) — typisch für Externe, die Workflows nur auslösen, nicht definieren sollten." },
  RECH_FREIGABE: { key: "RECH_FREIGABE", name: "Rechnungsfreigabe erweitert", objectsTotal: 35, removable: true, cluster: "Einkauf",
    values: [ruleAV("M_RECH_BUK", "01"), ruleAV("M_RECH_BUK", "81")],
    rationale: "M_RECH_BUK ACTVT 01/81 (Advanced, Rechnungsprüfung anlegen/freigeben) in einer Rolle, die überwiegend Anzeige braucht." },
  FIGM: { key: "FIGM", name: "Zuschussverwaltung", objectsTotal: 27, removable: true, cluster: "Büro", ohneKurztext: true,
    values: [ruleAV("F_FIGM_GNT", "01"), ruleAV("F_FIGM_GNT", "G1")],
    rationale: "F_FIGM_GNT ACTVT 01/G1 (Advanced) — Fördermittelverwaltung, in Sammelrollen historisch mitgeschleppt." },
  USERADM: { key: "USERADM", name: "Benutzerverwaltung", objectsTotal: 19, removable: true, cluster: "IT",
    values: [ruleAV("S_USER_GRP", "01"), ruleAV("S_USER_GRP", "22")],
    rationale: "S_USER_GRP ACTVT 01/22 (Advanced) — Benutzeranlage in einer IT-Rolle, die eigentlich Monitoring abdeckt." },
  KNA1_PFLEGE: { key: "KNA1_PFLEGE", name: "Kundenstamm pflegen", objectsTotal: 33, removable: true, cluster: "Büro",
    values: [ruleAV("F_KNA1_APP", "01"), ruleAV("F_KNA1_APP", "02")],
    rationale: "F_KNA1_APP ACTVT 01/02 (Core) — Kundenstammpflege hebt Anzeige-Nutzer von Self-Service auf Core." },
  LFA1_PFLEGE: { key: "LFA1_PFLEGE", name: "Lieferantenstamm pflegen", objectsTotal: 30, removable: true, cluster: "Büro",
    values: [ruleAV("F_LFA1_APP", "05"), ruleAV("F_LFA1_BUK", "06")],
    rationale: "F_LFA1_APP ACTVT 05 und F_LFA1_BUK ACTVT 06 (Core) — Lieferantenpflege, die Anzeige-Nutzer auf Core hebt." },
} satisfies Record<string, SingleTemplate>;

type TKey = keyof typeof T;

// Sammelrollen-Vorlagen: bündeln Einzelrollen, KEINE eigenen Werte.
const C: Record<string, { key: string; name: string; singles: TKey[] }> = {
  LOG_ALLG: { key: "LOG_ALLG", name: "Logistik Allgemein", singles: ["FI_BUCHEN", "LAGER_BEWEG", "FI_VOLL"] },
  LOG_CORE: { key: "LOG_CORE", name: "Logistik Core", singles: ["FI_BUCHEN", "LAGER_BEWEG"] },
  BUERO_ALLG: { key: "BUERO_ALLG", name: "Büro Allgemein", singles: ["VERTRIEB", "FI_ANZEIGE", "BELEG_VOLL"] },
  BUERO_CORE: { key: "BUERO_CORE", name: "Büro Core", singles: ["VERTRIEB", "FI_ANZEIGE"] },
  EXTERN_SAMMEL: { key: "EXTERN_SAMMEL", name: "Externe Sammelrolle", singles: ["FI_ANZEIGE", "WF_SONDER"] },
  EINKAUF_ALLG: { key: "EINKAUF_ALLG", name: "Einkauf Allgemein", singles: ["EINKAUF_ANZ", "RECH_FREIGABE"] },
  FI_ALLG: { key: "FI_ALLG", name: "Finanzen Allgemein", singles: ["FI_BUCHEN", "FIGM"] },
  IT_ALLG: { key: "IT_ALLG", name: "IT Allgemein", singles: ["IT_MONITOR", "USERADM"] },
  STAMM_KUNDE: { key: "STAMM_KUNDE", name: "Stammdaten Kunde", singles: ["FI_ANZEIGE", "KNA1_PFLEGE"] },
  STAMM_LIEF: { key: "STAMM_LIEF", name: "Stammdaten Lieferant", singles: ["EINKAUF_ANZ", "LFA1_PFLEGE"] },
  HANDEL_CORE: { key: "HANDEL_CORE", name: "Handel Core", singles: ["HANDEL", "EINKAUF_ANZ"] },
  SELF: { key: "SELF", name: "Self-Service", singles: ["FI_ANZEIGE"] },
  SELF_EINKAUF: { key: "SELF_EINKAUF", name: "Self-Service Einkauf", singles: ["EINKAUF_ANZ"] },
  ADMIN_MULTI: { key: "ADMIN_MULTI", name: "Administration (mehrsystemig)", singles: ["ADMIN_BASIS"] },
  WF_ALLG: { key: "WF_ALLG", name: "Workflow Allgemein", singles: ["WF_ADMIN", "FI_ANZEIGE"] },
};

export function generate(): Dataset {
  const rnd = mulberry32(SEED);

  // ---------- Landschaften: Systeme + Mandanten-Pools (Σ = 312) ----------
  const primarySid = (l: Landscape) => l.systems[0] ?? "GP7";
  const sidOf = (lid: string) => primarySid(landscapes.find((x) => x.id === lid)!);
  const lidOfSid = (sid: string) => assignedLandscapes.find((l) => primarySid(l) === sid)?.id ?? "L6";
  const clientPools = new Map<string, string[]>();
  for (const l of assignedLandscapes) {
    const pool: string[] = [];
    for (let c = 0; c < l.clients; c++) pool.push(String(100 + c).padStart(3, "0"));
    clientPools.set(l.id, pool);
  }
  const fueLids = assignedLandscapes.filter((l) => isFueRelevantSid(primarySid(l))).map((l) => l.id);
  const sizeWeights = assignedLandscapes.map((l) => l.clients);
  const wsum = sizeWeights.reduce((a, b) => a + b, 0);
  const pickLandscape = (): string => {
    let r = rnd() * wsum;
    for (let i = 0; i < sizeWeights.length; i++) { r -= sizeWeights[i]; if (r <= 0) return assignedLandscapes[i].id; }
    return assignedLandscapes[0].id;
  };
  /** Eine FUE-Landschaft, die nicht in `not` ist — für Zweit-/Drittkonten. */
  const pickFueLandscape = (not: string[]): string => {
    for (let i = 0; i < 20; i++) { const l = pickLandscape(); if (fueLids.includes(l) && !not.includes(l)) return l; }
    return fueLids.find((l) => !not.includes(l)) ?? fueLids[0];
  };
  const pickClient = (lid: string): string => {
    const pool = clientPools.get(lid) ?? ["100"];
    return pool[Math.floor(rnd() * pool.length)];
  };

  // ---------- Rollen: je System instanziiert ----------
  const singleRoles: SingleRole[] = [];
  const compositeRoles: CompositeRole[] = [];
  const singleById = new Map<string, SingleRole>();
  const compById = new Map<string, CompositeRole>();

  function single(key: TKey, sid: string): SingleRole {
    const id = `Z_${key}_${sid}`;
    const have = singleById.get(id);
    if (have) return have;
    const t = T[key] as SingleTemplate;
    const c = classifyValues(t.values);
    const r: SingleRole = {
      id, name: t.ohneKurztext ? "" : `${t.name} (${sid})`, landscapeId: lidOfSid(sid), sid, template: key, objectsTotal: t.objectsTotal,
      authValues: t.values, forcedClass: c.cls, forcedStep: c.step, engines: c.engines,
      removable: t.removable, leverId: t.removable ? `H-${key}-${sid}` : undefined,
    };
    singleRoles.push(r); singleById.set(id, r);
    return r;
  }
  function composite(key: string, sid: string): CompositeRole {
    const id = `Z_${key}_${sid}`;
    const have = compById.get(id);
    if (have) return have;
    const t = C[key];
    const r: CompositeRole = { id, name: `${t.name} (${sid})`, landscapeId: lidOfSid(sid), sid, template: key, singleRoleIds: t.singles.map((k) => single(k, sid).id) };
    compositeRoles.push(r); compById.set(id, r);
    return r;
  }
  // Engine-Rollen (echte LoB-Objekte) — nur auf ihrem System.
  const engineRole = (sid: string, key: string, name: string, values: AuthValue[], engine: EngineMetric): SingleRole => {
    const id = `Z_${key}_${sid}`;
    const have = singleById.get(id); if (have) return have;
    const c = classifyValues(values);
    const r: SingleRole = { id, name: `${name} (${sid})`, landscapeId: lidOfSid(sid), sid, template: key, objectsTotal: 14, authValues: values, forcedClass: c.cls, forcedStep: c.step, engines: c.engines.length ? c.engines : [engine] };
    singleRoles.push(r); singleById.set(id, r); return r;
  };
  const srGts = engineRole("GSP", "ENG_GTS", "GTS Engine-Nutzung", [ruleAV("/ECRS/DVI"), ruleAV("F_BKPF_BUK", "06")], "GTS");
  const srEwm = engineRole("GP7", "ENG_EWM", "EWM Engine-Nutzung", [ruleAV("/SCDL/AUTH"), ruleAV("F_BKPF_BUK", "06")], "EWM");
  const srTm = engineRole("TPM", "ENG_TM", "TM Engine-Nutzung", [ruleAV("T_AGR_FR"), ruleAV("F_BKPF_BUK", "06")], "TM");

  // ---------- Personen & Konten ----------
  const persons: Person[] = [];
  const accounts: Account[] = [];
  const accById = new Map<string, Account>();
  let pIdx = 0;
  let aIdx = 0;
  const pad6 = (n: number) => String(n).padStart(6, "0");

  type RoleSpec = { composites?: string[]; singles?: string[]; isDev?: boolean };
  /** Rollen-Spezifikation: Vorlagen-Schlüssel → auf dem System des Kontos instanziieren. */
  const spec = (lid: string, comps: string[], singles: string[] = [], isDev = false): RoleSpec => {
    const sid = sidOf(lid);
    return { composites: comps.map((k) => composite(k, sid).id), singles: singles.map((k) => single(k as TKey, sid).id), isDev };
  };

  function addAccount(p: Person, lid: string, roleSpec: RoleSpec, opts: { username: string; pn: string; email?: string; trace: TraceState; locked?: boolean; lastLogon?: string; client?: string }): Account {
    const sid = sidOf(lid);
    const client = opts.client ?? pickClient(lid);
    const singleIds = new Set<string>(roleSpec.singles ?? []);
    for (const cid of roleSpec.composites ?? []) for (const sr of compById.get(cid)?.singleRoleIds ?? []) singleIds.add(sr);
    const values: AuthValue[] = [];
    const engines = new Set<EngineMetric>();
    for (const sid2 of singleIds) {
      const sr = singleById.get(sid2);
      if (!sr) continue;
      values.push(...sr.authValues);
      for (const e of sr.engines) engines.add(e);
    }
    const c = classifyValues(values, roleSpec.isDev);
    const account: Account = {
      id: "USER-" + pad6(aIdx++), personId: p.id, sid, client, username: opts.username,
      personalnummer: opts.pn, email: opts.email, lastLogon: opts.lastLogon, locked: opts.locked ?? false,
      compositeRoleIds: roleSpec.composites ?? [], singleRoleIds: roleSpec.singles ?? [], isDev: roleSpec.isDev,
      accountClass: c.cls, accountStep: c.step, engines: [...engines], trace: opts.trace,
    };
    accounts.push(account);
    accById.set(account.id, account);
    p.accountIds.push(account.id);
    if (!p.landscapeIds.includes(lid)) p.landscapeIds.push(lid);
    return account;
  }

  // Klassifikation, Personen-Finalisierung und Maßnahmen: src/domain/build.ts (gemeinsam mit dem Seed-Loader).

  function newPerson(type: PersonType, primary: string): Person {
    const p: Person = {
      id: "P-" + pad6(4812 + pIdx++), type, unit: assignedLandscapes.find((l) => l.id === primary)?.unit ?? "Arvato SE",
      accountIds: [], landscapeIds: [primary], primaryLandscape: primary,
      licenseClass: "NotRelevant", cleanClass: "NotRelevant", fue: 0, drivers: [], leverIds: [],
    };
    persons.push(p);
    return p;
  }
  let userSeq = 0;
  function nextUser(): { user: string; pn: string; email: string } {
    const n = userSeq++;
    return { user: "U" + pad6(n), pn: pad6(n + 1), email: `u${pad6(n)}@arvato.example` };
  }

  /**
   * Eine Person mit einem Primärkonto und beliebig vielen weiteren Konten.
   * Jedes Konto: Landschaft, Rollen (Vorlagen), eigener Trace.
   */
  interface AccPlan { lid: string; comps: string[]; singles?: string[]; isDev?: boolean; trace: TraceState; lastLogon?: string; locked?: boolean; client?: string }
  function makePerson(type: PersonType, primary: AccPlan, others: AccPlan[] = [], opts: { split?: boolean; missingEmail?: boolean } = {}): Person {
    const p = newPerson(type, primary.lid);
    const u = nextUser();
    addAccount(p, primary.lid, spec(primary.lid, primary.comps, primary.singles, primary.isDev), { username: u.user, pn: u.pn, email: u.email, trace: primary.trace, lastLogon: primary.lastLogon, locked: primary.locked, client: primary.client });
    others.forEach((o, i) => {
      const user = opts.split && i === 0 ? u.user + "X" : u.user;
      const email = opts.missingEmail && i === 0 ? undefined : u.email;
      const pn = i === 0 ? "00" + u.pn : u.pn; // führende Nullen (normalisiert gleich)
      addAccount(p, o.lid, spec(o.lid, o.comps, o.singles, o.isDev), { username: user, pn, email, trace: o.trace, lastLogon: o.lastLogon, locked: o.locked, client: o.client });
    });
    return p;
  }
  const created: Person[] = [];

  // ============================================================
  //  Kohorten — krumme Größen, Summe 60.000 (Rest ist die Core-Basis)
  // ============================================================

  // A) Über-Grant-Kohorten: je (Vorlage, System) eine Maßnahme mit EIGENER Trace-Mischung.
  //    Daraus entsteht der berechnete Aufwand: viel „nie genutzt" → niedrig, viel „genutzt" → hoch.
  interface Lift { key: TKey; lid: string; comp: string; type: PersonType; g: number; a: number; gr: number; second: string; lastLogon?: string }
  const LIFTS: Lift[] = [
    { key: "FI_VOLL", lid: "L6", comp: "LOG_ALLG", type: "lager", g: 2917, a: 1604, gr: 781, second: "LOG_CORE" },
    { key: "FI_VOLL", lid: "L5", comp: "LOG_ALLG", type: "lager", g: 1283, a: 214, gr: 96, second: "LOG_CORE" },
    { key: "BELEG_VOLL", lid: "L2", comp: "BUERO_ALLG", type: "buero", g: 641, a: 383, gr: 207, second: "BUERO_CORE" },
    { key: "BELEG_VOLL", lid: "L8", comp: "BUERO_ALLG", type: "buero", g: 412, a: 58, gr: 37, second: "BUERO_CORE" },
    { key: "WF_SONDER", lid: "L10", comp: "EXTERN_SAMMEL", type: "extern", g: 233, a: 391, gr: 109, second: "SELF", lastLogon: "2026-05-01" },
    { key: "WF_SONDER", lid: "L3", comp: "EXTERN_SAMMEL", type: "extern", g: 178, a: 61, gr: 44, second: "SELF", lastLogon: "2026-04-14" },
    { key: "RECH_FREIGABE", lid: "L6", comp: "EINKAUF_ALLG", type: "lager", g: 154, a: 812, gr: 137, second: "SELF_EINKAUF" },
    { key: "RECH_FREIGABE", lid: "L9", comp: "EINKAUF_ALLG", type: "lager", g: 297, a: 88, gr: 51, second: "SELF_EINKAUF" },
    { key: "FIGM", lid: "L2", comp: "FI_ALLG", type: "buero", g: 122, a: 341, gr: 66, second: "LOG_CORE" },
    { key: "USERADM", lid: "L5", comp: "IT_ALLG", type: "it", g: 87, a: 204, gr: 41, second: "SELF" },
    { key: "USERADM", lid: "L7", comp: "IT_ALLG", type: "it", g: 143, a: 39, gr: 22, second: "SELF" },
    { key: "KNA1_PFLEGE", lid: "L6", comp: "STAMM_KUNDE", type: "buero", g: 1042, a: 317, gr: 158, second: "SELF" },
    { key: "KNA1_PFLEGE", lid: "L10", comp: "STAMM_KUNDE", type: "buero", g: 268, a: 402, gr: 131, second: "SELF" },
    { key: "LFA1_PFLEGE", lid: "L2", comp: "STAMM_LIEF", type: "buero", g: 356, a: 127, gr: 74, second: "SELF_EINKAUF" },
  ];
  for (const lift of LIFTS) {
    const buckets: [number, TraceState][] = [[lift.g, "nie-genutzt"], [lift.a, "genutzt"], [lift.gr, "kein-trace"]];
    for (const [n, trace] of buckets) {
      for (let i = 0; i < n; i++) {
        const second = pickFueLandscape([lift.lid]);
        created.push(makePerson(lift.type,
          { lid: lift.lid, comps: [lift.comp], trace, lastLogon: lift.lastLogon },
          [{ lid: second, comps: [lift.second], trace: "genutzt" }]));
      }
    }
  }

  // B) Überschneidung (317): tragen FI_VOLL@GP7 UND WF_SONDER@GPY (I5).
  for (let i = 0; i < 317; i++) {
    created.push(makePerson("extern",
      { lid: "L6", comps: ["LOG_ALLG"], trace: "genutzt" },
      [{ lid: "L10", comps: ["EXTERN_SAMMEL"], trace: "genutzt" }]));
  }

  // C) Gehalten (873): tragen einen entfernbaren Über-Grant, fallen aber NICHT,
  //    weil ADMIN_BASIS (nicht entfernbar, Advanced) sie oben hält — betroffen, nicht wirksam (R3).
  const heldPlan: [string, string, number][] = [["LOG_ALLG", "L6", 583], ["BUERO_ALLG", "L2", 196], ["EXTERN_SAMMEL", "L10", 94]];
  for (const [comp, lid, n] of heldPlan) {
    for (let i = 0; i < n; i++) {
      created.push(makePerson("it",
        { lid, comps: [comp], singles: ["ADMIN_BASIS"], trace: "genutzt" },
        [{ lid: pickFueLandscape([lid]), comps: ["ADMIN_MULTI"], trace: "genutzt" }]));
    }
  }

  // D) Advanced, mehrsystemig, ohne Potenzial (8.643): Administration auf zwei Systemen.
  for (let i = 0; i < 8643; i++) {
    const prim = pickFueLandscape([]);
    created.push(makePerson(i % 3 === 0 ? "it" : "buero",
      { lid: prim, comps: ["ADMIN_MULTI"], trace: "genutzt" },
      [{ lid: pickFueLandscape([prim]), comps: ["ADMIN_MULTI"], trace: "genutzt" }]));
  }

  // E) Developer (293): #DEV# auf FP6, Core-Konto auf GP7.
  for (let i = 0; i < 293; i++) {
    created.push(makePerson("entwickler",
      { lid: "L5", comps: [], isDev: true, trace: "genutzt" },
      [{ lid: "L6", comps: ["LOG_CORE"], trace: "genutzt" }]));
  }

  // H) Mehr-Landschafts-Personen (2.611) — drei bis fünf Landschaften, GEMISCHTE Klassen.
  //    Das ist der Beweis für R1: ohne Konsolidierung würden sie drei- bis fünffach lizenziert.
  //    Ein Teil hat zusätzlich ein GSP-Konto (Engine) — FUE-Klasse und Engine-Nutzung nebeneinander.
  for (let i = 0; i < 1204; i++) { // 3 Landschaften: Advanced (WF_ADMIN) + Core + Self-Service
    const a = pickFueLandscape([]); const b = pickFueLandscape([a]); const c = pickFueLandscape([a, b]);
    created.push(makePerson(i % 4 === 0 ? "it" : "buero",
      { lid: a, comps: ["WF_ALLG"], trace: "genutzt" },
      [{ lid: b, comps: ["LOG_CORE"], trace: "genutzt" }, { lid: c, comps: ["SELF"], trace: "kein-trace" }]));
  }
  for (let i = 0; i < 873; i++) { // 4 Landschaften: Core + Core + SS + SS, jede vierte mit GSP-Engine-Konto
    const a = pickFueLandscape([]); const b = pickFueLandscape([a]); const c = pickFueLandscape([a, b]);
    const d = i % 4 === 0 ? "L1" : pickFueLandscape([a, b, c]);
    created.push(makePerson("lager",
      { lid: a, comps: ["LOG_CORE"], trace: "genutzt" },
      [{ lid: b, comps: ["HANDEL_CORE"], trace: "genutzt" }, { lid: c, comps: ["SELF"], trace: "nie-genutzt" },
       d === "L1" ? { lid: "L1", comps: [], singles: [], trace: "genutzt" } : { lid: d, comps: ["SELF_EINKAUF"], trace: "kein-trace" }]));
  }
  for (let i = 0; i < 534; i++) { // 5 Landschaften: Advanced + Core + Core + SS + SS
    const used: string[] = [];
    const pick = () => { const l = pickFueLandscape(used); used.push(l); return l; };
    const a = pick(), b = pick(), c = pick(), d = pick(), e = pick();
    created.push(makePerson("buero",
      { lid: a, comps: ["WF_ALLG"], trace: "genutzt" },
      [{ lid: b, comps: ["LOG_CORE"], trace: "genutzt" }, { lid: c, comps: ["BUERO_CORE"], trace: "genutzt" },
       { lid: d, comps: ["SELF"], trace: "nie-genutzt" }, { lid: e, comps: ["SELF_EINKAUF"], trace: "kein-trace" }]));
  }

  // I) Nur auf Systemen mit eigener Metrik (2.014): GSP (GTS-Engine) und HCM — keine FUE-Klasse.
  for (let i = 0; i < 1171; i++) created.push(makePerson("lager", { lid: "L1", comps: [], singles: [], trace: "genutzt" }));
  for (let i = 0; i < 843; i++) created.push(makePerson("buero", { lid: "L4", comps: ["SELF"], trace: "genutzt" }));

  // M) Mehrere Mandanten im SELBEN System (1.729): GP7 hat 110 Mandanten — ein Sachbearbeiter hat
  //    dort typischerweise Konten in vielen davon. Eine Person, viele Konten, EIN FUE (R1). Jede
  //    fünfte trägt in einem der Mandanten zusätzlich den Über-Grant der Landschaft.
  const MULTI: { lid: string; n: number; min: number; max: number; comp: string; extra: string; type: PersonType }[] = [
    { lid: "L6", n: 1317, min: 3, max: 8, comp: "LOG_CORE", extra: "LOG_ALLG", type: "lager" },
    { lid: "L2", n: 412, min: 2, max: 4, comp: "BUERO_CORE", extra: "BUERO_ALLG", type: "buero" },
  ];
  for (const m of MULTI) {
    const pool = clientPools.get(m.lid)!;
    for (let i = 0; i < m.n; i++) {
      const k = m.min + Math.floor(rnd() * (m.max - m.min + 1));
      // k verschiedene Mandanten dieses Systems
      const chosen: string[] = [];
      while (chosen.length < k) { const c = pool[Math.floor(rnd() * pool.length)]; if (!chosen.includes(c)) chosen.push(c); }
      const others: AccPlan[] = chosen.slice(1).map((client, j) => ({
        lid: m.lid, client, comps: [j === 0 && i % 5 === 0 ? m.extra : m.comp], trace: j % 3 === 1 ? "nie-genutzt" : "genutzt",
      }));
      created.push(makePerson(m.type, { lid: m.lid, client: chosen[0], comps: [m.comp], trace: "genutzt" }, others));
    }
  }

  // G) Self-Service (6.421): 487 mit Zweitkonto.
  for (let i = 0; i < 6421; i++) {
    const prim = pickFueLandscape([]);
    created.push(makePerson("buero",
      { lid: prim, comps: [i % 5 === 0 ? "SELF_EINKAUF" : "SELF"], trace: "kein-trace" },
      i < 487 ? [{ lid: pickFueLandscape([prim]), comps: ["SELF"], trace: "kein-trace" }] : []));
  }

  // F) Core-Basis — der Rest bis 60.000. Gestreut über alle FUE-Landschaften, gemischte Sammelrollen.
  const REST = 60_000 - created.length;
  const coreComps = ["LOG_CORE", "BUERO_CORE", "HANDEL_CORE", "LOG_CORE"];
  for (let i = 0; i < REST; i++) {
    const prim = pickFueLandscape([]);
    const has2nd = i < REST - 1_589;
    const missingEmail = has2nd && i < 1_187;
    const lastLogon = i >= 5_000 && i < 6_318 ? "2026-03-09" : undefined; // 1.318 Karteileichen in der Basis
    created.push(makePerson(i % 2 === 0 ? "lager" : "buero",
      { lid: prim, comps: [coreComps[i % coreComps.length]], trace: "genutzt", lastLogon },
      has2nd ? [{ lid: pickFueLandscape([prim]), comps: [i % 3 === 0 ? "SELF_EINKAUF" : "SELF"], trace: i % 7 === 0 ? "nie-genutzt" : "kein-trace" }] : [],
      { missingEmail }));
  }

  // ---------- Kollisionen (40 Konten, 20 Paare): mehrdeutiger Benutzername (I14) ----------
  for (let k = 0; k < 40; k++) {
    const p = created[20_000 + k];
    const prim = accById.get(p.accountIds[0])!;
    const collUser = "COLL" + String(Math.floor(k / 2)).padStart(4, "0");
    addAccount(p, p.primaryLandscape, spec(p.primaryLandscape, ["LOG_CORE"]), { username: collUser, pn: prim.personalnummer!, email: prim.email, trace: "kein-trace" });
  }

  // ---------- Technische User (4.427, NICHT FUE-relevant, I7) ----------
  const technicalCount = 4427;
  for (let t = 0; t < technicalCount; t++) {
    const l = assignedLandscapes[t % assignedLandscapes.length];
    accounts.push({
      id: "USER-" + pad6(aIdx++), personId: "T-" + pad6(t), sid: primarySid(l), client: pickClient(l.id), username: "RFC_" + pad6(t),
      personalnummer: "T" + pad6(t), email: undefined, locked: false, licTypeMaintained: "Kommunikation",
      compositeRoleIds: [], singleRoleIds: [], accountClass: "NotRelevant", accountStep: 9999, engines: [], trace: "kein-trace",
    });
  }

  // ---------- Engine-Rollen anhängen: GTS→alle GSP-Konten, EWM→GP7 (1.183), TM→TPM (1.097) ----------
  const engineAttach: { sid: string; sr: SingleRole; metric: EngineMetric; cap: number }[] = [
    { sid: "GSP", sr: srGts, metric: "GTS", cap: 99_999 }, { sid: "GP7", sr: srEwm, metric: "EWM", cap: 1183 }, { sid: "TPM", sr: srTm, metric: "TM", cap: 1097 },
  ];
  for (const ea of engineAttach) {
    let attached = 0;
    for (const a of accounts) {
      if (attached >= ea.cap) break;
      if (a.personId.startsWith("P-") && a.sid === ea.sid && !a.singleRoleIds.includes(ea.sr.id)) {
        a.singleRoleIds.push(ea.sr.id);
        if (!a.engines.includes(ea.metric)) a.engines.push(ea.metric);
        attached++;
      }
    }
  }

  // ---------- Rollen ohne Träger (RFP 2.2 unassigned roles) ----------
  // Je Landschaft 2–3 Alt-/Projektrollen, die niemandem zugewiesen sind: zwei Einzelrollen (eine davon
  // ohne lizenzrelevante Werte → keine Lizenzwirkung) und eine Sammelrolle. Sie ändern keine Person
  // und keine Kalibrierungszahl — nur die Rollenliste. Kein Zufall nötig (deterministisch je System).
  for (const l of assignedLandscapes) {
    const sid = primarySid(l);
    const alt = T.FI_ANZEIGE; const c1 = classifyValues(alt.values);
    const altId = `Z_ALT_FI_ANZEIGE_${sid}`;
    if (!singleById.has(altId)) {
      const r: SingleRole = { id: altId, name: `${alt.name} (alt) (${sid})`, landscapeId: lidOfSid(sid), sid, template: "FI_ANZEIGE", objectsTotal: alt.objectsTotal, authValues: alt.values, forcedClass: c1.cls, forcedStep: c1.step, engines: c1.engines };
      singleRoles.push(r); singleById.set(altId, r);
    }
    const prjId = `Z_PRJ_TEST_${sid}`;
    if (!singleById.has(prjId)) {
      // Projektrolle ohne Regeltreffer: nur Objekte ohne Lizenzwirkung (authValues leer).
      const r: SingleRole = { id: prjId, name: "", landscapeId: lidOfSid(sid), sid, template: "PRJ_TEST", objectsTotal: 18, authValues: [], forcedClass: "NotRelevant", forcedStep: 9999, engines: [] };
      singleRoles.push(r); singleById.set(prjId, r);
    }
    const compId = `Z_ALT_SELF_${sid}`;
    if (!compById.has(compId)) {
      const r: CompositeRole = { id: compId, name: `Self-Service (alt) (${sid})`, landscapeId: lidOfSid(sid), sid, template: "SELF", singleRoleIds: [altId] };
      compositeRoles.push(r); compById.set(compId, r);
    }
  }

  // ---------- Konten klassifizieren, Personen finalisieren, Maßnahmen ableiten (gemeinsame Logik) ----------
  const built = buildDataset({
    persons, accounts, singleRoles, compositeRoles,
    clusterOf: (t) => (T as Record<string, SingleTemplate>)[t]?.cluster,
    rationaleOf: (t) => (T as Record<string, SingleTemplate>)[t]?.rationale,
  });

  // ---------- Benutzername-Splits (§8) ----------
  function splitPass(cls: LicenseClass, count: number) {
    let done = 0;
    for (const p of persons) {
      if (done >= count) break;
      if (p.licenseClass !== cls || p.accountIds.length < 2) continue;
      const a1 = accById.get(p.accountIds[0]); const a2 = accById.get(p.accountIds[1]);
      if (!a1 || !a2 || a2.username !== a1.username || !a2.email) continue;
      a2.username = a2.username + "X"; done++;
    }
  }
  splitPass("Advanced", 1973);
  splitPass("Core", 5318);
  splitPass("SelfService", 487);

  const levers = built.levers;
  const roles = built.roles;

  // ---------- Entitlement-Register, Snapshots (gemeinsame Helfer, s. u.) ----------
  const entitlements = baseEntitlements();
  let heute = 0;
  for (const p of persons) heute += weight(p.licenseClass, DEFAULT_RATIO);
  const snapshots = snapshotsFor(Math.round(heute));

  return {
    persons, accounts, roles, singleRoles, compositeRoles, levers, entitlements, snapshots, landscapes,
    technicalCount, meta: { generatedFrom: SEED, accountsTotal: accounts.length, pceVersion: "V1.70", source: "generator" },
  };
}


/**
 * Entitlement-Register — Vertragspositionen, für Generator und Seed-Loader identisch.
 * FUE: „vertraglich" ist die Vertragsgrundlage je Klasse (Store) — die Vertragsseite liest den
 * lebenden Wert aus dem Store, hier steht nur der Stand beim Laden. System-Entitlements: aus der
 * Systemeinstufung gezählt (Kategorie A/B/C = lizenzierte Systeme; D trägt keine eigene Lizenz).
 */
export function baseEntitlements(): Entitlement[] {
  const sids = new Set(assignedLandscapes.flatMap((l) => l.systems));
  const lizenzierteSysteme = [...sids].filter((sid) => licensingBySid(sid).model !== "keine").length;
  return [
    { metric: "FUE", label: "Full Use Equivalent (FUE)", contracted: contractedFue(), required: null, unit: "FUE", state: "angebunden", note: "Berechtigungsbasierter Bedarf gegen Vertrag — nur aus Systemen mit FUE-Metrik (Kategorie A/B). Vertraglich = Summe der Vertragsgrundlage je Klasse (Einstellungen)." },
    { metric: "SystemEntitlement", label: "System-Entitlements (S/4HANA)", contracted: lizenzierteSysteme, required: lizenzierteSysteme, unit: "Systeme", state: "angebunden", note: "Gezählt aus der Systemeinstufung: Systeme der Kategorien A (FUE), B (FUE + Engine) und C (eigene Metrik). Fiori-Frontend und HANA Cockpit (D) tragen keine eigene Lizenz." },
    { metric: "GTS", label: "GTS (eigene Metrik statt FUE)", contracted: null, required: null, unit: "Zolldokumente / Screenings pro Jahr", state: "offen", note: "GSP (L1) wird über diese Metrik abgerechnet, nicht über FUE. Öffentlich nicht belastbar dokumentiert — der Wert muss aus dem Vertrag kommen." },
    { metric: "HCM", label: "HCM (eigene Metrik statt FUE)", contracted: null, required: null, unit: "Employee (User-Metrik)", state: "offen", note: "HCM / FIORI (L4) wird über eine Employee-Metrik abgerechnet, nicht über FUE. Im Vertrag über eigene Positionen abgebildet — Ausprägung zu bestätigen." },
    { metric: "EWM", label: "EWM Advanced (Engine, zusätzlich zu FUE)", contracted: null, required: null, unit: "je 5.000 Lieferpositionen", state: "offen", note: "Basic ist im S/4HANA-Kern enthalten, nur Advanced ist zusätzlich lizenzpflichtig. Annahme: GP7 — Systemkürzel im Termin zu bestätigen." },
    { metric: "TM", label: "TM Advanced (Engine, zusätzlich zu FUE)", contracted: null, required: null, unit: "Freight Spend oder Revenue", state: "offen", note: "Der Standard-Versand ist im Kern enthalten, nur Advanced ist zusätzlich lizenzpflichtig. Annahme: TPM — Systemkürzel im Termin zu bestätigen." },
    { metric: "DigitalAccess", label: "Digital Access", contracted: null, required: null, unit: "Belege", state: "offen", note: "Indirekte Nutzung — nicht in FUE." },
    { metric: "BTP-CU", label: "BTP – Capacity Units", contracted: null, required: null, unit: "CU", state: "offen", note: "Noch nicht angebunden." },
  ];

}

/**
 * Ein einziger Snapshot: der heutige konsolidierte Stand (Ende des Trace-Zeitraums). Frühere
 * Stände wären erfundene Faktoren — der Verlauf nach vorn kommt aus der Cutover-Kette
 * (analysis.ts, prognoseJeJahr), nicht aus einer Zeitreihe.
 */
export function snapshotsFor(heute: number): Snapshot[] {
  return [{ date: TRACE_META.to, label: "heute", fueTotal: Math.round(heute), event: "Aktueller Stand (konsolidiert)" }];
}

// Singleton — einmal generieren, überall nutzen.
let _ds: Dataset | null = null;
export function dataset(): Dataset {
  if (!_ds) _ds = generate();
  return _ds;
}
